// @ts-nocheck
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactComponent as CloseIcon } from '../../../assets/Icons/lucideIcons/x.svg';
import { ReactComponent as DownloadIcon } from '../../../assets/Icons/lucideIcons/download.svg';
import { SolyticsIconButton } from '../../../package/components';
import SwalToast from '../swalToast';
import HtmlFrame from '../HtmlFrame';
import { useHtmlAttributeDownloadQuery } from '../../../features/configuration/attributeLibrary/api/attributeQueries';
import { downloadModelHtmlAttributePdf } from '../../../utils/attributeFileDownload';
import useAttribute from '../../../hooks/ModelInventory/useAttribute';

const DEFAULT_EMPTY_PREVIEW_MESSAGE = 'No HTML content available to preview.';

const HtmlContentDialog = ({
  open,
  onClose,
  dialogTitle,
  htmlContent,
  showPdfDownload,
  onDownloadPdf,
  isDownloadingPdf,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
    <DialogTitle sx={{ pr: 7 }}>
      <Box component="span" aria-live="polite" aria-atomic="true">
        {dialogTitle}
      </Box>
    </DialogTitle>
    {showPdfDownload ? (
      <Tooltip title="Download PDF">
        <span>
          <SolyticsIconButton
            aria-label="Download PDF"
            onClick={onDownloadPdf}
            title="Download PDF"
            disabled={isDownloadingPdf}
            sx={{ position: 'absolute', right: 56, top: 8 }}
          >
            {isDownloadingPdf ? (
              <CircularProgress size={18} />
            ) : (
              <DownloadIcon width={18} height={18} />
            )}
          </SolyticsIconButton>
        </span>
      </Tooltip>
    ) : null}
    <SolyticsIconButton
      aria-label="Close"
      onClick={onClose}
      title="Close"
      sx={{ position: 'absolute', right: 16, top: 8 }}
    >
      <CloseIcon width={18} height={18} />
    </SolyticsIconButton>
    <DialogContent dividers sx={{ height: '70vh', p: 0 }}>
      <HtmlFrame
        html={htmlContent}
        title={dialogTitle}
        sx={{ height: '100%', bgcolor: 'background.paper' }}
      />
    </DialogContent>
  </Dialog>
);

HtmlContentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dialogTitle: PropTypes.string.isRequired,
  htmlContent: PropTypes.string.isRequired,
  showPdfDownload: PropTypes.bool,
  onDownloadPdf: PropTypes.func,
  isDownloadingPdf: PropTypes.bool,
};

const HtmlPreviewDialogLink = ({
  htmlContent = '',
  attributeLabel = '',
  linkVariant = 'body8',
  linkLabel = '',
  dialogTitle = '',
  emptyContentMessage = DEFAULT_EMPTY_PREVIEW_MESSAGE,
  showFallbackWhenEmpty = true,
  noWrap = false,
  linkSx = {},
  attributeId = null,
  entityId = null,
  showPdfDownload = false,
}) => {
  const { execute: fetchFile } = useHtmlAttributeDownloadQuery({
    options: { enabled: false, loader: false },
  });
  const [open, setOpen] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const previewHtml = useMemo(
    () => (htmlContent == null ? '' : String(htmlContent)),
    [htmlContent]
  );

  const hasPreviewHtml = useMemo(
    () => previewHtml.trim().length > 0,
    [previewHtml]
  );

  const resolvedLinkLabel =
    linkLabel || `View ${attributeLabel || 'Attribute'}`;
  const resolvedDialogTitle =
    dialogTitle || `View ${attributeLabel || 'Attribute'}`;

  const handleOpen = useCallback(() => {
    if (!hasPreviewHtml) {
      SwalToast({ icon: 'info', title: emptyContentMessage });
      return;
    }
    setOpen(true);
  }, [emptyContentMessage, hasPreviewHtml]);

  const handleClose = useCallback(() => setOpen(false), []);

  const handleDownloadPdf = useCallback(async () => {
    if (isDownloadingPdf) return;
    setIsDownloadingPdf(true);
    try {
      await downloadModelHtmlAttributePdf({
        fetchFile,
        entityId,
        attributeId,
        attributeLabel,
      });
    } finally {
      setIsDownloadingPdf(false);
    }
  }, [attributeId, attributeLabel, fetchFile, entityId, isDownloadingPdf]);

  if (!hasPreviewHtml && showFallbackWhenEmpty) {
    return (
      <Typography variant="body8" component="span">
        -
      </Typography>
    );
  }

  return (
    <>
      <Typography
        variant={linkVariant}
        component="button"
        type="button"
        onClick={handleOpen}
        noWrap={noWrap}
        aria-label={`Preview HTML file ${resolvedLinkLabel}`}
        title={resolvedLinkLabel}
        sx={{
          p: 0,
          border: 0,
          background: 'transparent',
          color: 'primary.main',
          cursor: 'pointer',
          textDecoration: 'underline',
          textAlign: 'left',
          maxWidth: '100%',
          ...linkSx,
        }}
      >
        {resolvedLinkLabel}
      </Typography>

      <HtmlContentDialog
        open={open}
        onClose={handleClose}
        dialogTitle={resolvedDialogTitle}
        htmlContent={previewHtml}
        showPdfDownload={showPdfDownload}
        onDownloadPdf={handleDownloadPdf}
        isDownloadingPdf={isDownloadingPdf}
      />
    </>
  );
};

HtmlPreviewDialogLink.propTypes = {
  htmlContent: PropTypes.string,
  attributeLabel: PropTypes.string,
  linkVariant: PropTypes.string,
  linkLabel: PropTypes.string,
  dialogTitle: PropTypes.string,
  emptyContentMessage: PropTypes.string,
  showFallbackWhenEmpty: PropTypes.bool,
  noWrap: PropTypes.bool,
  linkSx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  attributeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showPdfDownload: PropTypes.bool,
};

/** Renders a "View {attributeLabel}" link that fetches HTML content on click (lazy-load). */
export const LazyHtmlAttributePreviewLink = ({
  attribute,
  entityId,
  showPdfDownload = false,
}) => {
  const { execute: fetchFile } = useHtmlAttributeDownloadQuery({
    options: { enabled: false, loader: false },
  });
  const [open, setOpen] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const { getHtmlAttributeValue } = useAttribute();

  const attributeLabel = attribute?.label || attribute?.name || 'Attribute';
  const resolvedLabel = `View ${attributeLabel}`;

  const handleOpen = useCallback(async () => {
    setIsFetching(true);
    try {
      const res = await getHtmlAttributeValue({
        entity_id: entityId,
        attribute_id: attribute?.id,
      });
      const content = res?.data?.[attribute?.id] ?? '';
      setHtmlContent(content);
      setOpen(true);
    } catch {
      SwalToast({ icon: 'error', title: 'Failed to load HTML content.' });
    } finally {
      setIsFetching(false);
    }
  }, [attribute?.id, entityId, getHtmlAttributeValue]);

  const handleClose = useCallback(() => setOpen(false), []);

  const handleDownloadPdf = useCallback(async () => {
    if (isDownloadingPdf) return;
    setIsDownloadingPdf(true);
    try {
      await downloadModelHtmlAttributePdf({
        fetchFile,
        entityId,
        attributeId: attribute?.id,
        attributeLabel,
      });
    } finally {
      setIsDownloadingPdf(false);
    }
  }, [attribute?.id, attributeLabel, fetchFile, entityId, isDownloadingPdf]);

  return (
    <>
      <Typography
        variant="body2"
        component="button"
        type="button"
        onClick={handleOpen}
        disabled={isFetching}
        aria-label={`Preview HTML file ${resolvedLabel}`}
        title={resolvedLabel}
        sx={{
          p: 0,
          border: 0,
          background: 'transparent',
          color: 'primary.main',
          cursor: isFetching ? 'default' : 'pointer',
          textDecoration: 'underline',
          textAlign: 'left',
          maxWidth: '100%',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {resolvedLabel}
        {isFetching && <CircularProgress size={12} sx={{ ml: 0.5 }} />}
      </Typography>

      <HtmlContentDialog
        open={open}
        onClose={handleClose}
        dialogTitle={resolvedLabel}
        htmlContent={htmlContent}
        showPdfDownload={showPdfDownload}
        onDownloadPdf={handleDownloadPdf}
        isDownloadingPdf={isDownloadingPdf}
      />
    </>
  );
};

LazyHtmlAttributePreviewLink.propTypes = {
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    label: PropTypes.string,
  }),
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showPdfDownload: PropTypes.bool,
};

export default HtmlPreviewDialogLink;
